import * as Oidc from 'oidc-client';
import { UserManager, UserManagerSettings, User, SignoutResponse, Profile } from 'oidc-client';
import { updateMessage } from '@sg/pre-app-init';
import { SgAuthAdapter, BaseUserInfo, BaseRenewTokenResult } from '@sg/common';

declare module '../../../../common/src/lib/auth-adapter' {
  interface SgAuthAdapterMap {
    oidc: SgOidcLogin;
  }
}

export function isSignIn(): boolean {
  return /\/oidc-signin(?:\/none)?$/.test(location.pathname);
}

export function isSignOut(): boolean {
  return /\/oidc-signout$/.test(location.pathname);
}

export function isRenew(): boolean {
  return /\/oidc-renew$/.test(location.pathname);
}

export function isLoggedInUser(user: any): boolean {
  return 'undefined' !== typeof user && null !== user;
}

function isUserObject(user: any): user is User {
  if (user && user.id_token) {
    return true;
  }
  return false;
}

function userToOidcUser(user: User): OidcUser {
  if (user) {
    const givenName = user.profile.given_name || '';
    const familyName = user.profile.family_name || '';
    const oidcUser = new User(user as any) as OidcUser;
    oidcUser.userName = `${givenName} ${familyName}`;
    return oidcUser;
  }
}

function processIncoming(um: UserManager): { isRedirect: boolean; promise: Promise<OidcUser | SignoutResponse> } {
  if (isSignIn()) {
    history.pushState(null, '', `/${location.hash}`);
    return { isRedirect: true, promise: um.signinRedirectCallback().then(userToOidcUser) };
  } else if (isSignOut()) {
    history.pushState(null, '', `/${location.hash}`);
    return { isRedirect: true, promise: um.signoutRedirectCallback() };
  } else if (isRenew()) {
    history.pushState(null, '', `/${location.hash}`);
    return { isRedirect: true, promise: um.signinSilentCallback().then((user) => null) };
  } else {
    return { isRedirect: false, promise: um.getUser().then(userToOidcUser) };
  }
}

export interface OidcUser extends BaseUserInfo<Profile>, User {}

let sgOidcLogin: SgOidcLogin;

export class SgOidcLogin implements SgAuthAdapter {
  readonly type: 'oidc' = 'oidc';
  readonly userManager: UserManager;

  get isRedirect(): boolean | null {
    return this._isRedirect;
  }

  get ready(): Promise<OidcUser | null> {
    return this._userPromise ? this._userPromise : Promise.resolve(this.user);
  }

  get user(): OidcUser | null {
    return this._user;
  }

  /**
   * Returns the token from cache.
   */
  get token(): string | undefined {
    return (!this.user.expired && this.user.access_token) || undefined;
  }

  get userProfile(): any {
    return this.user && this.user.profile;
  }

  get metadataSvc(): Oidc.MetadataService {
    if (!this._metadataSvc) {
      this._metadataSvc = new (<any>Oidc).MetadataService(this.settings);
    }
    return this._metadataSvc;
  }

  private _user: OidcUser | null = null;
  private _userPromise: Promise<OidcUser | null>;
  private _metadataSvc: Oidc.MetadataService;
  private _isRedirect: boolean | null = null;

  private constructor(
    public readonly settings: UserManagerSettings,
    public readonly appName: string
  ) {
    this.userManager = new UserManager(settings);

    const incoming = processIncoming(this.userManager);
    this._userPromise = Promise.resolve()
      .then(() => incoming.promise)
      .then((user) => {
        if (!isLoggedInUser(user)) {
          return null;
        } else {
          this._user = isUserObject(user) ? (this._user = user) : undefined;
          this._isRedirect = incoming.isRedirect;
          updateMessage('Login verified, loading application');
          return this._user;
        }
      });
  }

  /**
   * Calculated number of seconds the access token has remaining.
   */
  expiresIn(): number {
    return (this.user.expires_at * 1000 - Date.now()) / 1000;
  }

  /**
   * Performs a silent renew.
   *
   * The returned promise does not reject on error, it will always resolve.
   * When an error is thrown the RenewTokenResultType.Error enum is used to reflect that.
   * @param force When true will force a renew when the user exists but token is expired
   */
  renewToken(force = false): Promise<BaseRenewTokenResult> {
    const p = this.ready.then(async (loggedUser) => {
      if (!isLoggedInUser(loggedUser)) {
        return <any>Promise.resolve({ result: 'notLoggedIn' as 'notLoggedIn' });
      } else {
        try {
          await this.userManager.clearStaleState();
        } catch {
          console.warn('OIDC User manager clear stale state failed');
        }
        return <any>(
          this.userManager
            .signinSilent()
            .then((user) => ({ result: 'success' as 'success', user: (this._user = userToOidcUser(user)) }))
        );
      }
    });

    return <any>p.catch((error) => ({ result: 'error' as 'error', error }));
  }

  /**
   * Returns the token, if not exist try's to renew it.
   */
  getToken(): Promise<string> {
    return this.ready.then(() => {
      // `ready` resolves to the page-load user, which goes stale once a renewal replaces `user`; checking its
      // `expired` would force a silent renew on every call after the first token lifetime.
      const loggedUser = this.user;
      if (!loggedUser) {
        throw new Error('Not Logged In');
      } else if (loggedUser.expired) {
        return this.renewToken().then((result) => {
          if (result.result === 'success') {
            return this.user.access_token;
          } else {
            throw result.error;
          }
        });
      } else {
        return this.user.access_token;
      }
    });
  }

  signinRedirect(oidcArgs?: any): Promise<any> {
    updateMessage('Login required, redirecting to login page');
    const args: any = oidcArgs ? { ...oidcArgs } : {};
    return this.userManager.signinRedirect(args);
  }

  signoutRedirect(): Promise<void> {
    return this.userManager.signoutRedirect();
  }

  static get(): SgOidcLogin {
    if (!sgOidcLogin) {
      throw new Error('SgOidcLogin not set.');
    }
    return sgOidcLogin;
  }

  static create(settings: UserManagerSettings, appName: string): SgOidcLogin {
    if (sgOidcLogin) {
      throw new Error('SgOidcLogin already set.');
    }

    let uris = {
      redirect_uri: `${location.origin}/oidc-signin`,
      post_logout_redirect_uri: `${location.origin}/oidc-signout`,
      silent_redirect_uri: `${location.origin}/oidc-renew`,
    };

    if (location.hostname === 'localhost' || location.hostname.startsWith('api')) {
      uris = {
        redirect_uri: `${location.origin}/${appName}/oidc-signin`,
        post_logout_redirect_uri: `${location.origin}/${appName}/oidc-signout`,
        silent_redirect_uri: `${location.origin}/${appName}/oidc-renew`,
      };
    }

    settings = Object.assign(
      {
        ...uris,
        response_type: 'id_token token',
        //response_mode: 'form_post', //will result in "Cannot POST .../oidc-signin" until Http verb is allowed
        scope: 'openid api1',
      },
      settings
    );
    return (sgOidcLogin = new SgOidcLogin(settings, appName));
  }
}
